'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import React from 'react';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { buttonVariants } from '../ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Badge } from '../ui/badge';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { RxArrowTopRight } from 'react-icons/rx';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export default function FirstBlog({ blog }: { blog: Blog }) {
  const locale = useLocaleStore(state => state.currentLocale);
  const { t } = useTranslations();

  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  return (
    <div className="flex w-full items-center justify-center pr-6">
      <Link
        prefetch={false}
        href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
        className={cn(
          'custom-container',
          'relative flex h-[400px] flex-col items-start justify-end gap-4 overflow-hidden rounded-[16px] p-4 sm:h-[420px] sm:gap-6 sm:rounded-[20px] sm:p-6 md:h-[450px] lg:rounded-[24px]',
        )}
      >
        {!!blog.categories.length && (
          <Badge size={'sm'} variant={'white'} className="absolute left-4 top-4 z-[5]">
            {blog?.categories[0]?.name}
          </Badge>
        )}
        <div className="relative z-10 flex w-full max-w-[795px] flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
          <div className="flex w-full flex-col items-start justify-start gap-2">
            <span className="text-xs text-white/70 sm:text-sm">{formattedDate}</span>
            <h2 data-ea-visual-tag="h4" className="line-clamp-2 text-xl font-medium tracking-tight text-white sm:text-2xl lg:text-3xl xl:text-4xl">
              {blog.name}
            </h2>
          </div>
          <div className="flex items-center justify-start gap-2.5">
            <span
              aria-hidden="true"
              data-ea-visual-tag="button"
              className={cn(buttonVariants({ size: 'icon' }), 'bg-white/20 text-sm font-semibold transition-all hover:bg-white/30 sm:text-base')}
            >
              <RxArrowTopRight size={20} className="text-white" />
            </span>
            <p className={cn('text-sm font-semibold text-white sm:text-base', 'transition-all')}>
              {t('text1148')}
            </p>
          </div>
        </div>
        <div className={cn('absolute inset-0 z-[1] h-full w-full', 'ea-first-blog__overlay')} />
        <Image
          src={blog.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes="100vw"
        />
      </Link>
    </div>
  );
}
